import React, { useEffect } from 'react'
import { useLogic } from '~common/ui-logic/hooks/logic'
import { useAppContext } from '~/app-context'
import { useCacheValue } from '~/cache-context'
import type { CachedData } from '~/services/cache/types'
import { Button } from '~common/components/button'
import DropdownMenu from '~common/components/dropdown-menu'
import { ManageBankAccountsLogic } from './logic'
import { type ManageBankAccountsLogicDependencies } from './logic'
import type { UserBankAccount } from '~/storage/types'

export interface ManageBankAccountsProps
    extends ManageBankAccountsLogicDependencies {}

export function ManageBankAccounts(props: ManageBankAccountsProps) {
    const appContext = useAppContext()
    const { logic } = useLogic(ManageBankAccountsLogic, {
        ...props,
        ...appContext,
    })

    const bankAccounts = useCacheValue('bankAccounts') || {}
    const bankAccountsArray = Object.values(bankAccounts) as UserBankAccount[]
    const selectedIban = useCacheValue(
        'selectedAccountIban' as keyof CachedData,
    )
    const selectedAccount = bankAccountsArray.find(
        (account) => account.iban === selectedIban,
    )
    const buttonLabel = selectedAccount ? selectedAccount.name : 'Manage'

    const loadState = useCacheValue('loadState')
    if (loadState === 'running') {
        return (
            <Button
                label="Loading..."
                variant="secondary"
                size="medium"
                fullWidth
                maxWidth="150px"
                disabled
            />
        )
    }

    if (loadState === 'error') {
        return (
            <Button
                label="Error"
                variant="ghost"
                size="medium"
                fullWidth
                maxWidth="150px"
                disabled
            />
        )
    }

    if (bankAccountsArray.length === 0) {
        return (
            <Button
                label="Add Bank Account"
                onClick={() => logic.addBankAccount()}
                variant="primary"
                size="medium"
                icon="bank"
                maxWidth="200px"
            />
        )
    }

    const dropdownItems = [
        ...bankAccountsArray.map((account) => ({
            id: account.iban,
            label: account.name,
            onClick: () => logic.selectAccount(account),
        })),
        {
            id: 'add-new',
            label: 'Add New Bank Account/Transactions',
            icon: 'plus',
            onClick: () => logic.addBankAccount(),
        },
    ]

    return (
        <DropdownMenu
            trigger={
                <Button
                    label={buttonLabel}
                    icon="bank"
                    variant="secondary"
                    size="medium"
                    fullWidth
                    maxWidth="150px"
                />
            }
            items={dropdownItems}
            placement="bottom"
        />
    )
}
